import { useEffect } from 'react';
import useSWR, { mutate } from 'swr';

import { FixMe } from '@utils/types';
import useStores from '@stores/useStores';
import { ApiGetResource, fetchData, swrConfigDefaults } from '@api';

import { AccountManagementStore } from '../store';

/**
 * Whether the account list in the store can be trusted.
 *
 * `ready` is the only state in which an empty list means "this workspace has no
 * accounts"; in `loading` and `error` it means "we don't know yet / we couldn't find
 * out". Modelled as a tri-state rather than a `boolean` + `error` pair because the
 * distinction is load-bearing: routing a user with saved accounts into a first-run
 * Create flow on the strength of an empty list is a destructive misread.
 */
type AccountsLoadState = 'loading' | 'ready' | 'error';

export const getResourceCacheKey = (
  workspaceId: string,
  resourceDefinitionName: string,
  apiResourceName: ApiGetResource,
) => `${workspaceId}-${resourceDefinitionName}-${apiResourceName}`;

export const useLoadAccountsIntoAccountManagementStore = ({
  accountManagementStore,
  legacyAccountsRole,
}: {
  accountManagementStore: AccountManagementStore;
  legacyAccountsRole?: string;
}) => {
  const { workspaceStore, messagesStore, accountDefinitionsListStore } = useStores();
  const {
    setAccountDefinitions,
    setAccounts,
    setLegacyAccounts,
    supportedAccountTypes,
    resourceDefinitionName,
  } = accountManagementStore;

  // Fetch accounts(migrated) only if the destination supports accounts types

  const {
    isLoading: loadingAccounts,
    accountsError,
    accountsApiResponse,
  } = (() => {
    if (supportedAccountTypes.length > 0) {
      const {
        isLoading,
        error,
        data: accountsApiResponse,
      } = useSWR(
        getResourceCacheKey(workspaceStore.id, resourceDefinitionName, ApiGetResource.ACCOUNTS),
        fetchData({
          path: `/web/workspaces/${workspaceStore.id}`,
          resource: ApiGetResource.ACCOUNTS,
          errorMessage: 'Failed to fetch destination accounts',
          params: {
            definitionNames: supportedAccountTypes.join(','),
          },
          onError: (e) => messagesStore.showErrorMessage(e),
        }),
        swrConfigDefaults,
      );

      return { isLoading, accountsError: error, accountsApiResponse };
    }

    return { isLoading: false, accountsError: undefined, accountsApiResponse: undefined };
  })();

  // Fetch legacy accounts
  const path = `/workspaces/${workspaceStore.id}/destination/accounts/${legacyAccountsRole}`;

  const {
    isLoading: loadingLegacyAccounts,
    error: legacyAccountsError,
    data: legacyAccountsApiResponse,
  } = useSWR(
    legacyAccountsRole
      ? getResourceCacheKey(
          workspaceStore.id,
          resourceDefinitionName,
          ApiGetResource.ACCOUNTS_LEGACY,
        )
      : null,
    fetchData<ApiGetResource.ACCOUNTS_LEGACY>({
      path,
      errorMessage: 'Failed to fetch destination accounts',
      onError: (e) => messagesStore.showErrorMessage(e),
    }),
    swrConfigDefaults,
  );

  useEffect(() => {
    // Set account definitions from the store
    if (accountDefinitionsListStore.accountDefinitions.length > 0) {
      setAccountDefinitions(accountDefinitionsListStore.accountDefinitions);
    }

    if (accountsApiResponse) {
      setAccounts(accountsApiResponse.data);
    }
    if (legacyAccountsApiResponse) {
      setLegacyAccounts(legacyAccountsApiResponse as FixMe);
    }
  }, [
    accountsApiResponse,
    legacyAccountsApiResponse,
    accountDefinitionsListStore.accountDefinitions,
  ]);

  const isLoading =
    loadingAccounts || loadingLegacyAccounts || !accountDefinitionsListStore.firstLoad;

  // SWR reports `isLoading: false` the moment a fetch *fails*, so `!isLoading` means
  // "settled", not "loaded" — a failed fetch leaves the store's account list empty and
  // indistinguishable from a workspace that genuinely has none. Callers that branch on
  // the list (e.g. "no accounts → go straight to Create") must be able to tell those
  // apart, so the load is reported as an explicit tri-state rather than leaving each
  // call site to remember to check `error` alongside `isLoading`.
  //
  // `accountDefinitionsListStore` is loaded once per session outside SWR and is only
  // marked `firstLoad` on success, so its failure is folded in here too — otherwise it
  // reads as a load that never finishes.
  const loadState: AccountsLoadState = (() => {
    if (accountsError || legacyAccountsError || accountDefinitionsListStore.loadFailed) {
      return 'error';
    }
    return isLoading ? 'loading' : 'ready';
  })();

  /** Re-run whichever of the two independent loads is in play. */
  const retry = () => {
    if (supportedAccountTypes.length > 0) {
      mutate(
        getResourceCacheKey(workspaceStore.id, resourceDefinitionName, ApiGetResource.ACCOUNTS),
      );
    }
    if (accountDefinitionsListStore.loadFailed) {
      accountDefinitionsListStore.getAccountDefinitions();
    }
  };

  return { isLoading, loadState, retry };
};
